// Schedule Publication Management Hook
import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { tryResolveTenantId } from '../utils/tenant';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc,
  doc,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { updateShiftTimecard } from '../utils/shiftAttendance';

export const useSchedulePublication = (weekStart) => {
  const [publicationStatus, setPublicationStatus] = useState(null);
  const [publicationHistory, setPublicationHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!weekStart) return;
    
    const fetchPublicationStatus = async () => {
      setLoading(true);
      try {
  const weekKey = require('../utils/shiftDate').toIsoDateString(weekStart); // YYYY-MM-DD format
        
        const tenantId = await tryResolveTenantId();
        if (!tenantId) throw new Error('Tenant not resolved');

        const publicationsQuery = query(
          collection(db, `tenants/${tenantId}/schedulePublications`),
          where('weekStart', '==', weekKey)
        );

        const publicationsSnapshot = await getDocs(publicationsQuery);
        
        if (!publicationsSnapshot.empty) {
          const publicationData = publicationsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Get the most recent publication for this week
          const latestPublication = publicationData.sort((a, b) => 
            new Date(b.publishedAt) - new Date(a.publishedAt)
          )[0];
          
          setPublicationStatus(latestPublication);
          setPublicationHistory(publicationData);
        } else {
          setPublicationStatus(null);
          setPublicationHistory([]);
        }
      } catch (error) {
        console.error('Error fetching publication status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPublicationStatus();
  }, [weekStart]);

  const publishSchedule = async (publishedBy, notes = '') => {
    console.log('🚀 publishSchedule called with:', { publishedBy, notes, weekStart });
    if (!weekStart) throw new Error('No week specified');
    
    try {
  const weekKey = require('../utils/shiftDate').toIsoDateString(weekStart);
  const end = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
    const tenantId = await tryResolveTenantId();
    if (!tenantId) throw new Error('Tenant not resolved');
      
      // Create publication record
  const publicationData = {
  weekStart: weekKey,
  weekEnd: require('../utils/shiftDate').toIsoDateString(end),
        publishedAt: Timestamp.now(),
        publishedBy: publishedBy,
        notes: notes,
        version: (publicationHistory.length + 1)
      };
      
      const publicationRef = await addDoc(collection(db, `tenants/${tenantId}/schedulePublications`), publicationData);

      // Fetch shifts for this week using shiftDate (Timestamp)
      const shiftsQuery = query(
        collection(db, `tenants/${tenantId}/shifts`),
        where('shiftDate', '>=', Timestamp.fromDate(weekStart)),
        where('shiftDate', '<', Timestamp.fromDate(end))
      );
      const shiftsSnapshot = await getDocs(shiftsQuery);      
      if (!shiftsSnapshot.empty) {        const batch = writeBatch(db);
        const shiftsToBackfill = [];

        shiftsSnapshot.docs.forEach(shiftDoc => {
          const data = shiftDoc.data();
          const ref = shiftDoc.ref;

          // Apply pending create (draft new shifts)
          if (data.pendingCreate === true || data.isPublished === false) {
            batch.update(ref, {
              isPublished: true,
              pendingCreate: false,
              publishedAt: Timestamp.now(),
              publishedBy: publishedBy,
              publicationId: publicationRef.id
            });
            // schedule backfill for this shift
            shiftsToBackfill.push({ id: shiftDoc.id, data });
          }

          // Apply pending updates
          if (data.pendingUpdate && Object.keys(data.pendingUpdate).length > 0) {
            const { pendingUpdate } = data;
            batch.update(ref, {
              ...pendingUpdate,
              pendingUpdate: {},
              isPublished: true,
              publishedAt: Timestamp.now(),
              publishedBy: publishedBy,
              publicationId: publicationRef.id
            });
            // schedule backfill for this shift (pendingUpdate usually changes employee/date/time)
            shiftsToBackfill.push({ id: shiftDoc.id, data: { ...data, ...pendingUpdate } });
          }

          // Apply pending deletions
          if (data.pendingDelete === true) {
            batch.update(ref, {
              deleted: true,
              pendingDelete: false,
              isPublished: true,
              publishedAt: Timestamp.now(),
              publishedBy: publishedBy,
              publicationId: publicationRef.id
            });
            // do not backfill deletions
          }

          // Ensure already-published items are marked
          if (data.isPublished !== true && !data.pendingCreate && !data.pendingUpdate && !data.pendingDelete) {
            batch.update(ref, {
              isPublished: true,
              publishedAt: Timestamp.now(),
              publishedBy: publishedBy,
              publicationId: publicationRef.id
            });
            shiftsToBackfill.push({ id: shiftDoc.id, data });
          }
        });        await batch.commit();
        // After commit, run a best-effort backfill for any shifts that were just published/updated
        for (const s of shiftsToBackfill) {
          // run in background; don't block the publish flow
          backfillShiftTimeEntries(tenantId, s).catch(err => {
            console.error('backfillShiftTimeEntries error for shift', s.id, err);
          });
        }
      }
      
      // Update local state to reflect the publication
      const newPublicationStatus = { id: publicationRef.id, ...publicationData };      setPublicationStatus(newPublicationStatus);
      setPublicationHistory(prev => [...prev, newPublicationStatus]);      return publicationRef.id;
    } catch (error) {
      console.error('Error publishing schedule:', error);
      throw error;
    }
  };

  const unpublishSchedule = async (unpublishedBy, reason = '') => {
    if (!weekStart) throw new Error('No week specified');
    
    try {
  const weekKey = require('../utils/shiftDate').toIsoDateString(weekStart);
  const end = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
    const tenantId = await tryResolveTenantId();
    if (!tenantId) throw new Error('Tenant not resolved');
      
      // Record the unpublish action
      const unpublishData = {
  weekStart: weekKey,
  weekEnd: require('../utils/shiftDate').toIsoDateString(end),
        unpublishedAt: Timestamp.now(),
        unpublishedBy: unpublishedBy,
        reason: reason,
        previousPublicationId: publicationStatus?.id
      };
      await addDoc(collection(db, `tenants/${tenantId}/scheduleUnpublications`), unpublishData);

      // Mark shifts as unpublished for this week
      const shiftsQuery = query(
        collection(db, `tenants/${tenantId}/shifts`),
        where('shiftDate', '>=', Timestamp.fromDate(weekStart)),
        where('shiftDate', '<', Timestamp.fromDate(end))
      );
      const shiftsSnapshot = await getDocs(shiftsQuery);
      
      if (!shiftsSnapshot.empty) {
        const batch = writeBatch(db);
        shiftsSnapshot.docs.forEach(shiftDoc => {
          batch.update(shiftDoc.ref, {
            isPublished: false,
            unpublishedAt: Timestamp.now(),
            unpublishedBy: unpublishedBy
          });
        });
        await batch.commit();
      }
      
      setPublicationStatus(null);
      return true;
    } catch (error) {
      console.error('Error unpublishing schedule:', error);
      throw error;
    }
  };

  return {
    publicationStatus,
    publicationHistory,
    loading,
    publishSchedule,
    unpublishSchedule,
    isPublished: !!publicationStatus
  };
};

// Backfill helper: attach shiftId to existing top-level timeEntries for a shift and mirror them
async function backfillShiftTimeEntries(tenantId, shiftRecord) {
  try {
    if (!tenantId || !shiftRecord || !shiftRecord.id) return;
    const shiftId = shiftRecord.id;
    const shiftData = shiftRecord.data || shiftRecord;

    // Only attempt backfill for scheduled shifts with employeeId and a date
    const employeeId = shiftData.employeeId;
    let shiftDate = shiftData.shiftDate || shiftData.date;
    if (!employeeId || !shiftDate) return;

    // Normalize date string YYYY-MM-DD
  // Normalize shiftDate that might be a Timestamp, Date, or string
  const { toIsoDateString } = require('../utils/shiftDate');
  shiftDate = toIsoDateString(shiftDate);

    // Query timeEntries for that employee on the date that don't have shiftId set
    const teQuery = query(
      collection(db, `tenants/${tenantId}/timeEntries`),
      where('employeeId', '==', employeeId),
      where('date', '==', shiftDate)
    );
    const teSnap = await getDocs(teQuery);
    if (teSnap.empty) return;

    // For each entry lacking shiftId, set shiftId and mirror into shift subcollection
    for (const docSnap of teSnap.docs) {
      const e = { id: docSnap.id, ...docSnap.data() };
      // Skip voided entries; they should not be attached or mirrored into shifts
      if (e.status === 'voided') continue;
      if (e.shiftId) continue;
      // Attach shiftId on top-level entry
      await updateDoc(doc(db, `tenants/${tenantId}/timeEntries`, e.id), { shiftId });
    }

    // Refresh derived timecard for the parent shift
    await updateShiftTimecard(tenantId, shiftId);
  } catch (err) {
    console.error('backfillShiftTimeEntries error', err);
  }
}
